// =============================================================================
// screens/DMNewScreen.tsx — New Message / compose (route: DMNew, modal).
//
// MIGRATION (bare RN, P3 DMs / blueprint §2): ported off apps/cryb-native
// app/dm/new.tsx. Swaps: expo-router → react-navigation (useLocalSearchParams
// ?username → route.params?.username prefill; router.replace('/dm/:id') →
// navigation.replace('DM',{id}); router.back() → goBack; the inline
// <Stack.Screen title='New Message'> → navigation.setOptions). @expo/vector-icons
// → ../components/ui/Icon. RN Image (no expo-image here). The user search +
// debounce + selection + POST-then-open-conversation flow is unchanged.
//
//   GET  /users/search?q=…                          — user picker
//   POST /direct-messages/conversations {recipientId,content} — create + first msg
// =============================================================================
import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Alert,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale,
} from '../constants/theme';
import { GlassSurface, GlassCard, GlassButton } from '../components/glass';
import Asset3D from '../components/ui/Asset3D';
import ParticleBackground from '../components/ui/ParticleBackground';
import { api } from '../services/api';

type Nav = NativeStackNavigationProp<RootStackParamList, 'DMNew'>;
type DMNewRoute = RouteProp<RootStackParamList, 'DMNew'>;

interface UserResult {
  id: string;
  username: string;
  displayName?: string;
  avatar?: string;
}

export default function DMNewScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<DMNewRoute>();
  // When opened from a profile (DMNew { username }) pre-fill the search so the
  // intended recipient surfaces immediately instead of a blank picker.
  const prefillUsername = route.params?.username;
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<UserResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      title: 'New Message',
      headerStyle: { backgroundColor: colors.bg },
      headerTintColor: colors.ink,
      headerShadowVisible: false,
      headerTitleStyle: { ...fonts.semibold, fontSize: 17 },
    });
  }, [navigation]);

  const searchUsers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      // Backend wraps as { data: { users: [...], pagination } }.
      const res = await api<{ users?: UserResult[] }>(
        `/users/search?q=${encodeURIComponent(query.trim())}`
      );
      if (res.success && res.data) {
        setResults(Array.isArray(res.data.users) ? res.data.users
          : Array.isArray((res.data as any)?.items) ? (res.data as any).items : []);
      } else {
        setResults([]);
      }
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Clear any pending debounce timer on unmount so a search (→ setState) can't
  // fire after the screen has left (leaving mid-typing).
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // On mount, if we arrived with a username param, seed the search box and run
  // the lookup so the target user is one tap away.
  useEffect(() => {
    if (prefillUsername) {
      setSearch(prefillUsername);
      searchUsers(prefillUsername);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillUsername]);

  function handleSearchChange(text: string) {
    setSearch(text);
    if (selectedUser) {
      setSelectedUser(null);
    }
    // Debounced search: store the timer in a ref so each keystroke clears the
    // previous one. (Returning a cleanup fn from onChangeText does nothing — RN
    // ignores the return value, so the old code never actually debounced.)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchUsers(text);
    }, 300);
  }

  function handleSelectUser(user: UserResult) {
    setSelectedUser(user);
    setSearch(user.displayName || user.username);
    setResults([]);
  }

  async function handleSend() {
    if (!selectedUser || !messageText.trim()) return;
    setSending(true);

    const res = await api('/direct-messages/conversations', {
      method: 'POST',
      body: JSON.stringify({
        recipientId: selectedUser.id,
        content: messageText.trim(),
      }),
    });

    setSending(false);

    if (res.success && res.data) {
      const convoId =
        (res.data as any).conversation?.id ||
        (res.data as any).id ||
        (res.data as any).conversation_id;
      if (convoId) {
        navigation.replace('DM', { id: String(convoId) });
      } else {
        navigation.goBack();
      }
    } else {
      Alert.alert('Error', res.error || 'Failed to send message');
    }
  }

  function renderUserResult({ item }: { item: UserResult }) {
    const displayName = item.displayName || item.username || 'User';
    const initial = (displayName.charAt(0) || 'U').toUpperCase();
    return (
      <View style={styles.rowWrap}>
        <GlassCard
          padding={spacing.md}
          radius={radius.lg}
          contentStyle={styles.userRow}
          onPress={() => handleSelectUser(item)}
          accessibilityLabel={`Message ${displayName}`}
        >
          {item.avatar ? (
            <CachedImage source={{ uri: item.avatar }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, styles.avatarFallback]}>
              <Text style={styles.avatarLetter}>
                {initial}
              </Text>
            </View>
          )}
          <View style={styles.userInfo}>
            <Text style={styles.userName} numberOfLines={1}>
              {displayName}
            </Text>
            <Text style={styles.userHandle} numberOfLines={1}>@{item.username || 'user'}</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={colors.faint} />
        </GlassCard>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* Living animated background (design-system): deep premium glass surface. */}
      <ParticleBackground />

      {/* To Field — glass bar (leading label + spinner while searching). */}
      <View style={[styles.toFieldWrap, containerStyle]}>
        <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.toFieldContent}>
          <Text style={styles.toLabel}>To</Text>
          <TextInput
            style={styles.toInput}
            placeholder="Search people..."
            placeholderTextColor={colors.muted}
            value={search}
            onChangeText={handleSearchChange}
            autoFocus
            autoCorrect={false}
            autoCapitalize="none"
          />
          {searching ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : search.length > 0 ? (
            <TouchableOpacity
              onPress={() => { setSearch(''); setSelectedUser(null); setResults([]); }}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close-circle" size={18} color={colors.faint} />
            </TouchableOpacity>
          ) : null}
        </GlassSurface>
      </View>

      {/* Selected User Badge */}
      {selectedUser && (
        <View style={[styles.selectedBadgeWrap, containerStyle]}>
          <View style={styles.selectedBadge}>
            <Ionicons name="person" size={14} color={colors.primary} />
            <Text style={styles.selectedText} numberOfLines={1}>
              {selectedUser.displayName || selectedUser.username || 'User'}
            </Text>
            <TouchableOpacity
              onPress={() => {
                setSelectedUser(null);
                setSearch('');
              }}
              accessibilityRole="button"
              accessibilityLabel="Clear selected recipient"
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            >
              <Ionicons name="close-circle" size={18} color={colors.muted} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Search Results */}
      {!selectedUser && results.length > 0 && (
        <FlatList
          data={Array.isArray(results) ? results : []}
          keyExtractor={(item, index) => item?.id ?? String(index)}
          renderItem={renderUserResult}
          style={styles.resultsList}
          contentContainerStyle={[styles.resultsContent, containerStyle]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        />
      )}

      {/* Message Compose (shown when user is selected) */}
      {selectedUser && (
        <View style={[styles.composeSection, containerStyle]}>
          <GlassSurface variant="card" radius={radius.lg} style={styles.composeWrap} contentStyle={styles.composeContent}>
            <TextInput
              style={styles.composeInput}
              placeholder="Write your message..."
              placeholderTextColor={colors.muted}
              multiline
              maxLength={2000}
              value={messageText}
              onChangeText={setMessageText}
              textAlignVertical="top"
            />
          </GlassSurface>
        </View>
      )}

      {/* Send Bar */}
      {selectedUser && (
        <View style={[styles.sendBar, containerStyle, { paddingBottom: insets.bottom || spacing.md }]}>
          <GlassButton
            title="Send"
            icon="send"
            onPress={handleSend}
            loading={sending}
            disabled={!messageText.trim() || sending}
            fullWidth
            size="lg"
          />
        </View>
      )}

      {/* Empty state when no search and no selection */}
      {!selectedUser && results.length === 0 && search.length < 2 && (
        <View style={styles.emptyWrap}>
          <GlassCard style={styles.emptyCard} padding={spacing.xl} radius={radius.xl}>
            <Asset3D name="chat" size={120} style={{ marginBottom: spacing.md }} />
            <Text style={styles.emptyText}>Start a conversation</Text>
            <Text style={styles.emptySubtext}>
              Search for a name or username to send your first message.
            </Text>
          </GlassCard>
        </View>
      )}

      {/* No-match state (searched, nothing found) */}
      {!selectedUser && !searching && results.length === 0 && search.trim().length >= 2 && (
        <View style={styles.emptyWrap}>
          <GlassCard style={styles.emptyCard} padding={spacing.xl} radius={radius.xl}>
            <Asset3D name="compass" size={120} style={{ marginBottom: spacing.md }} />
            <Text style={styles.emptyText}>No people found</Text>
            <Text style={styles.emptySubtext}>
              Try a different name or username.
            </Text>
          </GlassCard>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  toFieldWrap: {
    marginHorizontal: layoutTokens.gutter,
    marginTop: spacing.md,
  },
  toFieldContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    minHeight: 48,
    gap: spacing.sm,
  },
  toLabel: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink2,
  },
  toInput: {
    flex: 1,
    ...typographyScale.body,
    color: colors.ink,
    padding: 0,
  },
  selectedBadgeWrap: {
    marginTop: spacing.sm,
    paddingHorizontal: layoutTokens.gutter,
  },
  selectedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: colors.primary + '14',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    gap: spacing.xs,
  },
  selectedText: {
    ...typographyScale.caption,
    ...fonts.semibold,
    fontSize: 13,
    color: colors.primary,
  },
  resultsList: {
    marginTop: spacing.sm,
    flex: 1,
  },
  resultsContent: {
    paddingBottom: layoutTokens.bottomNavInset,
  },
  // Row = shared GlassCard; this only separates rows in the list.
  rowWrap: {
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.sm,
  },
  userRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: radius.full,
  },
  avatarFallback: {
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    ...typographyScale.h3,
    fontSize: 16,
    color: colors.primary,
  },
  userInfo: {
    flex: 1,
    marginLeft: spacing.md,
  },
  userName: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
  },
  userHandle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
  },
  composeSection: {
    flex: 1,
    padding: layoutTokens.gutter,
  },
  composeWrap: {
    flex: 1,
  },
  composeContent: {
    flex: 1,
    padding: spacing.md,
  },
  composeInput: {
    flex: 1,
    ...typographyScale.body,
    color: colors.ink,
    padding: 0,
  },
  sendBar: {
    paddingHorizontal: layoutTokens.gutter,
    paddingTop: spacing.sm,
  },
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 64,
    height: 64,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
});
